import { jsPDF } from "jspdf";

const PAGE = {
  width: 210,
  height: 297,
  margin: 12,
};

const COLORS = {
  title: [34, 91, 39],
  cardBorder: [220, 220, 220],
  cardBg: [250, 250, 250],
  text: [40, 40, 40],
  label: [90, 90, 90],
};

export const isLandscapeTask = (taskRow) => taskRow?.taskType === "landscape";

export const getItemWorkerId = (item) => {
  if (!item) return null;
  const w = item.workerId;
  if (w && typeof w === "object") return String(w._id || "");
  return w ? String(w) : null;
};

export const hasLandscapeAttachments = (taskRow) => {
  const img = taskRow?.image || {};
  const before = img.beforeWorkImages || [];
  const after = img.afterWorkImages || [];
  const additional = img.additionalImages || [];
  const beforeComments = taskRow?.beforeComment || [];
  const afterComments = taskRow?.afterComment || [];
  return (
    before.length > 0 ||
    after.length > 0 ||
    additional.length > 0 ||
    beforeComments.length > 0 ||
    afterComments.length > 0
  );
};

export const canDownloadTaskAttachments = (taskRow) => {
  if (isLandscapeTask(taskRow)) return hasLandscapeAttachments(taskRow);
  return Array.isArray(taskRow?.subTask) && taskRow.subTask.length > 0;
};

const toMediaUrl = (item) => {
  if (item == null || item === "") return null;
  if (typeof item === "string") return item;
  if (typeof item === "object" && item.url) return String(item.url);
  return String(item);
};

const normalizeList = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value.map(toMediaUrl).filter(Boolean);
  }
  const single = toMediaUrl(value);
  return single ? [single] : [];
};

const workerNameFromSubtask = (subtask) => {
  if (subtask?.worker) {
    return `${subtask.worker.firstName || ""} ${subtask.worker.lastName || ""}`.trim();
  }
  return subtask?.employeeName || "";
};

const drawWrappedText = (doc, text, x, y, maxWidth, lineHeight = 5) => {
  const safeText = text ?? "";
  const lines = doc.splitTextToSize(String(safeText), maxWidth);
  lines.forEach((line, index) => {
    doc.text(line, x, y + index * lineHeight);
  });
  return y + lines.length * lineHeight;
};

const ensurePageSpace = (doc, y, neededHeight) => {
  if (y + neededHeight <= PAGE.height - PAGE.margin) return y;
  doc.addPage();
  return PAGE.margin;
};

const fileToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

const getImageFormat = (url, contentType = "") => {
  if (contentType.includes("png")) return "PNG";
  if (contentType.includes("webp")) return "WEBP";
  if (contentType.includes("jpg") || contentType.includes("jpeg")) return "JPEG";

  const lowerUrl = String(url || "").toLowerCase();
  if (lowerUrl.includes(".png")) return "PNG";
  if (lowerUrl.includes(".webp")) return "WEBP";
  return "JPEG";
};

const fetchImageData = async (url) => {
  const response = await fetch(url);
  if (!response.ok) throw new Error("Image fetch failed");
  const blob = await response.blob();
  const format = getImageFormat(url, blob.type);
  const dataUrl = await fileToDataUrl(blob);
  return { dataUrl, format };
};

const drawCardHeader = (doc, title, y) => {
  const contentWidth = PAGE.width - PAGE.margin * 2;
  doc.setDrawColor(...COLORS.cardBorder);
  doc.setFillColor(...COLORS.cardBg);
  doc.roundedRect(PAGE.margin, y, contentWidth, 12, 2, 2, "FD");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(...COLORS.title);
  doc.text(title, PAGE.margin + 4, y + 8);
  return y + 16;
};

const drawMetaRow = (doc, label, value, y, valueOffset = 33) => {
  const contentWidth = PAGE.width - PAGE.margin * 2;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(...COLORS.label);
  doc.text(`${label}:`, PAGE.margin, y);

  doc.setFont("helvetica", "normal");
  doc.setTextColor(...COLORS.text);
  return drawWrappedText(doc, value || "-", PAGE.margin + valueOffset, y, contentWidth - valueOffset, 4.5);
};

const drawImageSectionTitle = (doc, label, y) => {
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(...COLORS.label);
  doc.text(label, PAGE.margin, y);
  return y + 4;
};

const drawImagePlaceholder = (doc, x, y, width, height, text) => {
  doc.setDrawColor(200, 200, 200);
  doc.rect(x, y, width, height);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(120, 120, 120);
  doc.text(text, x + 2, y + height / 2);
};

const workerDisplayName = (worker) =>
  `${worker?.firstName || ""} ${worker?.lastName || ""}`.trim() || "Worker";

const filterByWorker = (list, workerId) =>
  (list || []).filter((item) => getItemWorkerId(item) === String(workerId));

const commentTexts = (comments) =>
  (comments || [])
    .map((c) => String(c?.text ?? "").trim())
    .filter(Boolean);

export const downloadTaskLandscapePdf = async (taskRow) => {
  if (!hasLandscapeAttachments(taskRow)) {
    return { ok: false, message: "No completed attachments found for this task" };
  }

  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const contentWidth = PAGE.width - PAGE.margin * 2;
  let y = PAGE.margin;

  const workers = Array.isArray(taskRow.worker) ? taskRow.worker : [];
  const imageBlock = taskRow.image || {};

  doc.setTextColor(...COLORS.title);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text("Landscape Task Report", PAGE.margin, y);

  y += 8;
  doc.setTextColor(...COLORS.text);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  y = drawWrappedText(
    doc,
    `Task: ${taskRow.name || "Untitled Task"} | Status: ${taskRow.status || "-"}`,
    PAGE.margin,
    y,
    contentWidth
  );
  y += 2;

  const taskMeta = [
    ["Start Date", taskRow.startDate || "-"],
    ["End Date", taskRow.endDate || "-"],
    ["Priority", taskRow.priority || "-"],
  ];
  taskMeta.forEach(([label, value]) => {
    y = drawMetaRow(doc, label, value, y) + 1;
  });

  y = ensurePageSpace(doc, y, 14);
  y = drawMetaRow(doc, "Worker Description", taskRow.workerDescription || "-", y) + 4;

  const drawImageList = async (label, urls) => {
    y = ensurePageSpace(doc, y, 12);
    y = drawImageSectionTitle(doc, label, y);

    if (!urls.length) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(...COLORS.text);
      doc.text("-", PAGE.margin + 2, y + 1);
      y += 6;
      return;
    }

    const imgWidth = 58;
    const imgHeight = 42;
    const gapX = 4;
    const gapY = 6;
    const maxPerRow = 3;
    let localX = PAGE.margin;
    let rowMaxY = y;

    for (let i = 0; i < urls.length; i += 1) {
      if (i > 0 && i % maxPerRow === 0) {
        localX = PAGE.margin;
        y = rowMaxY + gapY;
        rowMaxY = y;
      }

      y = ensurePageSpace(doc, y, imgHeight + 8);
      if (localX === PAGE.margin) {
        rowMaxY = y;
      }

      try {
        const { dataUrl, format } = await fetchImageData(urls[i]);
        doc.addImage(dataUrl, format, localX, y, imgWidth, imgHeight);
      } catch (err) {
        drawImagePlaceholder(doc, localX, y, imgWidth, imgHeight, "Image load failed");
      }

      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(...COLORS.label);
      doc.text(`#${i + 1}`, localX, y + imgHeight + 4);

      rowMaxY = Math.max(rowMaxY, y + imgHeight + 4);
      localX += imgWidth + gapX;
    }

    y = rowMaxY + 6;
  };

  const workersToRender =
    workers.length > 0
      ? workers
      : [{ _id: "all", firstName: "All", lastName: "Workers" }];

  for (let wIndex = 0; wIndex < workersToRender.length; wIndex += 1) {
    const worker = workersToRender[wIndex];
    const workerId = worker._id;

    if (wIndex > 0) {
      doc.addPage();
      y = PAGE.margin;
    }

    y = drawCardHeader(doc, workerDisplayName(worker), y);

    const beforeComments = filterByWorker(taskRow.beforeComment, workerId);
    const afterComments = filterByWorker(taskRow.afterComment, workerId);
    const beforeImages = normalizeList(
      filterByWorker(imageBlock.beforeWorkImages, workerId)
    );
    const afterImages = normalizeList(
      filterByWorker(imageBlock.afterWorkImages, workerId)
    );

    y = drawMetaRow(
      doc,
      "Before Comments",
      commentTexts(beforeComments).length
        ? commentTexts(beforeComments).join(" | ")
        : "-",
      y
    ) + 2;

    await drawImageList("Before Images", beforeImages);

    y = drawMetaRow(
      doc,
      "After Comments",
      commentTexts(afterComments).length
        ? commentTexts(afterComments).join(" | ")
        : "-",
      y
    ) + 2;

    await drawImageList("After Images", afterImages);
  }

  const datePart = new Date().toISOString().split("T")[0];
  const fileName = `landscape_task_${taskRow.name || "task"}_${datePart}.pdf`;
  doc.save(fileName.replace(/[\\/:*?"<>|]/g, "_"));

  return { ok: true };
};

export const downloadTaskCompletedAttachmentsPdf = async (taskRow) => {
  if (isLandscapeTask(taskRow)) return downloadTaskLandscapePdf(taskRow);
  return downloadTaskSubtasksPdf(taskRow);
};

export const downloadTaskSubtasksPdf = async (taskRow) => {
  if (!taskRow?.subTask || !Array.isArray(taskRow.subTask) || taskRow.subTask.length === 0) {
    return { ok: false, message: "No subtask details found for this task" };
  }

  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const contentWidth = PAGE.width - PAGE.margin * 2;
  let y = PAGE.margin;

  doc.setTextColor(...COLORS.title);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text("Task Subtask Report", PAGE.margin, y);

  y += 8;
  doc.setTextColor(...COLORS.text);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  y = drawWrappedText(
    doc,
    `Task: ${taskRow.name || "Untitled Task"} | Total Subtasks: ${taskRow.subTask.length}`,
    PAGE.margin,
    y,
    contentWidth
  );

  y += 3;

  for (let index = 0; index < taskRow.subTask.length; index += 1) {
    const subtask = taskRow.subTask[index];
    if (index > 0) {
      doc.addPage();
      y = PAGE.margin;
    }

    y = drawCardHeader(doc, `Subtask #${index + 1}`, y);

    const metaFields = [
      ["Location Name", subtask.locationName || "-"],
      ["Location Address", subtask.locationAddress || "-"],
      ["Location Number", subtask.locationNumber || "-"],
      ["Time In", subtask.timeIn || "-"],
      ["Time Out", subtask.timeOut || "-"],
      ["Worker", workerNameFromSubtask(subtask) || "-"],
      ["Completed", subtask.completed ? "Yes" : "No"],
    ];

    metaFields.forEach(([label, value]) => {
      y = drawMetaRow(doc, label, value, y) + 1;
    });

    const commentsList = normalizeList(subtask.comments);
    y = drawMetaRow(doc, "Comments", commentsList.length ? commentsList.join(" | ") : "-", y) + 2;

    y = ensurePageSpace(doc, y, 16);
    y = drawMetaRow(doc, "Instructions", subtask.instructions || "-", y) + 2;

    const drawImageList = async (label, urls) => {
      y = ensurePageSpace(doc, y, 12);
      y = drawImageSectionTitle(doc, label, y);

      if (!urls.length) {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.setTextColor(...COLORS.text);
        doc.text("-", PAGE.margin + 2, y + 1);
        y += 6;
        return;
      }

      const imgWidth = 58;
      const imgHeight = 42;
      const gapX = 4;
      const gapY = 6;
      const maxPerRow = 3;
      let localX = PAGE.margin;
      let rowMaxY = y;

      for (let i = 0; i < urls.length; i += 1) {
        if (i > 0 && i % maxPerRow === 0) {
          localX = PAGE.margin;
          y = rowMaxY + gapY;
          rowMaxY = y;
        }

        y = ensurePageSpace(doc, y, imgHeight + 8);
        if (localX === PAGE.margin) {
          rowMaxY = y;
        }

        try {
          const { dataUrl, format } = await fetchImageData(urls[i]);
          doc.addImage(dataUrl, format, localX, y, imgWidth, imgHeight);
        } catch (err) {
          drawImagePlaceholder(doc, localX, y, imgWidth, imgHeight, "Image load failed");
          doc.setFont("helvetica", "normal");
          doc.setFontSize(7);
          doc.setTextColor(...COLORS.text);
          drawWrappedText(doc, urls[i], localX, y + imgHeight + 3, imgWidth, 3.5);
        }

        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        doc.setTextColor(...COLORS.label);
        doc.text(`#${i + 1}`, localX, y + imgHeight + 4);

        rowMaxY = Math.max(rowMaxY, y + imgHeight + 4);
        localX += imgWidth + gapX;
      }

      y = rowMaxY + 6;
    };

    await drawImageList("Before Images", normalizeList(subtask.beforeImage));
    await drawImageList("After Images", normalizeList(subtask.afterImage));
    await drawImageList("Receipt", normalizeList(subtask.receipt));
  }

  const datePart = new Date().toISOString().split("T")[0];
  const fileName = `task_subtasks_${taskRow.name || "task"}_${datePart}.pdf`;
  doc.save(fileName.replace(/[\\/:*?"<>|]/g, "_"));

  return { ok: true };
};
